import { useInfiniteList, type CrudFilters } from '@refinedev/core';
import { IVendor } from '@admin-panel/types';
import { Select, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useDebounce } from '@admin-panel/hooks';
import { SKIP_ADMIN_VENDOR_HEADER } from '@admin-panel/providers';

export const VendorSelect = ({ ...props }: any) => {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const filters: CrudFilters = [];

  if (props.filters) filters.push(...props.filters);

  if (debouncedSearchValue) {
    filters.push({
      field: 'vendorBrandName',
      operator: 'contains',
      value: debouncedSearchValue,
    });
  }

  const {
    query: { isLoading, fetchNextPage, isFetchingNextPage },
    result: { data, hasNextPage },
  } = useInfiniteList<IVendor>({
    resource: 'vendors',
    pagination: {
      pageSize: 1000,
    },
    filters: filters,
    sorters: [{ field: 'vendorBrandName', order: 'asc' }],
    meta: {
      headers: {
        [SKIP_ADMIN_VENDOR_HEADER]: 'true',
      },
    },
  });

  const allVendors = useMemo(() => {
    return data?.pages?.flatMap(page => page.data) || [];
  }, [data]);

  const formatVendorLabel = (vendor: IVendor) => {
    if (vendor.vendorBrandOptionLabel) {
      return vendor.vendorBrandOptionLabel;
    }

    const brandName = vendor.brands?.[0]?.name;
    if (vendor.name && brandName) {
      return `${vendor.name} (${brandName})`;
    }

    return vendor.vendorBrandName ?? vendor.name ?? String(vendor.id);
  };

  const vendorOptions = useMemo(() => {
    const options = allVendors.map(vendor => ({
      label: formatVendorLabel(vendor),
      value: vendor.id,
    }));

    const selectedVendor: IVendor | undefined = props.selectedVendor;
    if (selectedVendor && !options.some(option => option.value === selectedVendor.id)) {
      options.unshift({
        label: formatVendorLabel(selectedVendor),
        value: selectedVendor.id,
      });
    }

    return options;
  }, [allVendors, props.selectedVendor]);

  const handleChange = (value: any, option: any) => {
    if (Array.isArray(value)) {
      value = value[0];
    }

    if (value === undefined || value === null || value === '') {
      props.setVendor?.(undefined);
      props.onChange?.(value, option);
      return;
    }

    const selectedVendor = allVendors.find(vendor => vendor.id === parseInt(value));

    if (selectedVendor) {
      props.setVendor?.(selectedVendor);
    }

    props.onChange?.(value, option);
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { target } = e;
    const element = target as HTMLDivElement;

    if (
      element.scrollTop + element.offsetHeight >= element.scrollHeight - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const normalizedValue = useMemo(() => {
    if (props.value === undefined || props.value === null || props.value === '') {
      return props.value;
    }

    if (Array.isArray(props.value)) {
      return props.value.map((value: any) =>
        value === undefined || value === null || value === '' ? value : Number(value),
      );
    }

    return Number(props.value);
  }, [props.value]);

  const normalizedDefaultValue = useMemo(() => {
    if (
      props.defaultValue === undefined ||
      props.defaultValue === null ||
      props.defaultValue === ''
    ) {
      return props.defaultValue;
    }

    if (Array.isArray(props.defaultValue)) {
      return props.defaultValue.map((value: any) =>
        value === undefined || value === null || value === '' ? value : Number(value),
      );
    }

    return Number(props.defaultValue);
  }, [props.defaultValue]);

  return (
    <Select
      options={vendorOptions}
      value={normalizedValue}
      defaultValue={normalizedDefaultValue}
      onChange={handleChange}
      onSelect={props.onSelect}
      onDeselect={props.onSelect}
      onSearch={setSearchValue}
      style={props.style}
      placeholder="Select Brand/Vendor"
      showSearch
      allowClear
      disabled={props.disabled}
      status={props.status}
      loading={isLoading}
      onPopupScroll={handlePopupScroll}
      popupRender={menu => (
        <>
          {menu}
          {isFetchingNextPage && (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <Spin size="small" />
            </div>
          )}
        </>
      )}
      filterOption={false}
    />
  );
};
